import { useContext, useMemo } from 'react';
import { mapValues, sortBy } from 'lodash';
import {
  CROP_ID_FOR_NONE_CROP,
  CROP_ID_FOR_UNASSIGNED_STEMS,
  CROP_VARIETY_ID_FOR_NONE,
  MULTIPLE,
  NONE,
  UNKNOWN_CROP,
  UNKNOWN_CROP_NAME,
} from '../../constants/fields';
import { MapContext } from '../../contexts/MapContext';
import {
  asDynamicDecimal,
  asQuantity,
  asRoundedDecimal,
  unitForValue,
} from '../../utils/formatters';
import { PanelMainGroup, PanelSection } from '../SidePanel';
import {
  CropSwatchPicker,
  CropVarietyDropdown,
  LabeledData,
  LabeledInput,
  SectionHiddenWarning,
} from './menu-components';
import RightMenuTabs from './RightMenuTabs';
import * as validators from '../../utils/validators';
import * as normalizers from '../../utils/normalizers';
import CropSelector from '../CropSelector';
import { DISTANCE_UNITS, ROW_UNITS } from '../../constants/units';
import InRowIcon from '../icons/InRowIcon';
import { getCropId, isNoneOrUnassigned } from '../../utils/meta';
import {
  cropNameForRow,
  cropVarietyIdForRow,
  patternIdForRow,
  rowHasPattern,
} from '../../utils/points-and-rows/rows';
import PatternListDropdown from '../PatternListDropdown';
import SelectedPatternDropdown from './SelectedPatternDropdown';
import { captureEvent } from '../../utils/analytics';
import CropVarietySelector from '../CropVarietySelector';
import { useToggleState } from '../../utils/hooks';
import { cropVarietyIdForPattern } from '../../utils/patterns';
import { FarmCropData } from './CropData';
import {
  rightMenuTabs,
  useRightMenuTabContext,
} from '../../contexts/RightMenuTabContext';
import { PopOutTabContainer } from './PopOutTab';

const CropDesignSection = ({
  activeRowIndexes,
  colors,
  crop,
  cropVarietyIds,
  onSelectPattern,
  readonly,
  section,
  showPatternDropdown,
  showMultipleCropsSelector,
}) => {
  const {
    crops: { updateCropColor },
    fields: { clearRowOverrides, updateRowOverrides, setRowOverrides },
  } = useContext(MapContext);

  const showVarietyDropdown =
    !showMultipleCropsSelector && !isNoneOrUnassigned(crop?.id);
  const hasMultipleCropVarieties = cropVarietyIds.size > 1;
  const hasNonNullCropVariety = [...cropVarietyIds].some((id) => id);
  // Toggle display of variety selector for active rows
  const [varietyIsOpen, toggleVarietyOpen] = useToggleState(
    hasNonNullCropVariety,
  );

  return (
    <PanelSection>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="m-0">Crop</h4>
        {
          // This is the first section, show the pattern dropdown
          showPatternDropdown && (
            <PatternListDropdown onSelectPattern={onSelectPattern} />
          )
        }
        {showVarietyDropdown && (
          <CropVarietyDropdown
            isOpen={varietyIsOpen}
            onClick={toggleVarietyOpen}
          />
        )}
      </div>
      <LabeledData
        label={
          activeRowIndexes.length > 1
            ? `${activeRowIndexes.length} rows`
            : `row ${activeRowIndexes[0] + 1}`
        }
        labelMinWidth={50}
      >
        <CropSwatchPicker
          crop={showMultipleCropsSelector ? UNKNOWN_CROP : crop}
          colorOptions={colors}
          color="light"
          onSelect={(color) => {
            if (showMultipleCropsSelector) {
              console.error(
                'Cannot select color when multiple crops are selected',
              );
              return;
            }
            updateCropColor(crop, color);
          }}
          readonly={readonly}
        />
        <CropSelector
          onCropSelect={(selectedCrop) => {
            if (
              section.crop?.id === selectedCrop.id &&
              section.crop?.name === selectedCrop.name
            ) {
              clearRowOverrides(section.id, activeRowIndexes);
            } else {
              setRowOverrides(section.id, activeRowIndexes, {
                cropId: selectedCrop.id,
                cropName: selectedCrop.name,
                inRowSpacing: selectedCrop.inRowSpacing,
              });
            }
          }}
          selectedCrop={showMultipleCropsSelector ? { name: MULTIPLE } : crop}
          showNoCropOption
        />
      </LabeledData>
      {varietyIsOpen && !showMultipleCropsSelector && (
        <LabeledData label="variety" labelMinWidth={50}>
          <CropVarietySelector
            id="row-override-crop-variety"
            key={crop?.id}
            cropId={crop?.id}
            cropVarietyId={
              hasMultipleCropVarieties ? MULTIPLE : [...cropVarietyIds][0]
            }
            onCropVarietySelect={(selectedCropVariety) => {
              captureEvent('crop-variety-select', {
                action: selectedCropVariety ? 'assign' : 'remove',
                location: 'row-override',
              });
              updateRowOverrides(section.id, activeRowIndexes, {
                cropVarietyId:
                  selectedCropVariety?.id === section.primaryCropVariety?.id
                    ? undefined // Clear override crop variety if it matches primary
                    : selectedCropVariety?.id || CROP_VARIETY_ID_FOR_NONE,
              });
            }}
          />
        </LabeledData>
      )}
    </PanelSection>
  );
};

const InspectTab = ({
  activeRowIndexes,
  allCrops,
  colors,
  cropCounts,
  cropVarietyCounts,
  readonly,
  rowLength,
  treeCount,
}) => {
  // Toggle display of variety stem counts
  const [varietiesIsOpen, toggleVarietiesOpen] = useToggleState();
  const hasCropVarieties = useMemo(
    () => Object.values(cropVarietyCounts).some((counts) => !!counts.length),
    [cropVarietyCounts],
  );

  return (
    <>
      <PanelSection>
        <h4>{activeRowIndexes.length === 1 ? 'Row' : 'Rows'}</h4>
        <LabeledData
          copyable
          label="length"
          unitSpec={ROW_UNITS}
          formatter={asDynamicDecimal}
        >
          {rowLength}
        </LabeledData>
        {activeRowIndexes.length > 1 && (
          <LabeledData
            copyable
            label="qty"
            formatter={asQuantity}
            unit={unitForValue('rows', 'row')}
          >
            {activeRowIndexes.length}
          </LabeledData>
        )}
        <LabeledData
          copyable
          label="stems"
          formatter={asQuantity}
          unit={unitForValue('stems', 'stem')}
        >
          {treeCount}
        </LabeledData>
      </PanelSection>
      <PanelSection>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="m-0">Crops</h4>
          {hasCropVarieties && (
            <CropVarietyDropdown
              isOpen={varietiesIsOpen}
              onClick={toggleVarietiesOpen}
              plural
            />
          )}
        </div>
        {allCrops.map((crop) => (
          <FarmCropData
            key={crop.id}
            className="mb-2"
            qty={cropCounts[crop.id] ?? 0}
            crop={crop}
            colors={colors}
            showVarieties={varietiesIsOpen}
            varieties={cropVarietyCounts[crop.id]}
            readonly={readonly}
          />
        ))}
      </PanelSection>
    </>
  );
};

const RowMenu = ({ section, activeRowIndexes, readonly, colors }) => {
  const { activeTab, setActiveTab, popOutTabOpen } = useRightMenuTabContext();
  const {
    crops: { availableCrops: cropMap, cropNameToId, cropVarietyIdToCropId },
    fields: { clearRowOverrides, updateRowOverrides, setRowOverrides },
    patterns: { inRowPatternsMap },
  } = useContext(MapContext);

  const [cropCounts, cropVarietyCounts] = useMemo(() => {
    const countsByCrop = section.points.features.reduce((acc, cropPoint) => {
      if (!activeRowIndexes.includes(cropPoint.properties.row)) return acc;
      const { cropName = UNKNOWN_CROP_NAME, row } = cropPoint.properties;
      const cropId = getCropId({ name: cropName }, cropNameToId);
      const rowOverride = section.rowOverrides[row];
      const patternId = patternIdForRow(rowOverride, section);
      const cropVarietyId = patternId
        ? cropVarietyIdForPattern(patternId, inRowPatternsMap, cropId)
        : cropVarietyIdForRow(
            rowOverride,
            section,
            cropVarietyIdToCropId,
            cropId,
          );

      const cropMeta = acc[cropId] || {
        treeCount: 0,
        cropSectionVarieties: {},
      };
      return {
        ...acc,
        [cropId]: {
          ...cropMeta,
          treeCount: cropMeta.treeCount + 1,
          cropSectionVarieties: {
            ...cropMeta.cropSectionVarieties,
            ...(cropVarietyId && {
              [cropVarietyId]:
                (cropMeta.cropSectionVarieties[cropVarietyId] ?? 0) + 1,
            }),
          },
        },
      };
    }, {});
    return [
      mapValues(countsByCrop, 'treeCount'),
      mapValues(countsByCrop, (counts) =>
        Object.entries(
          counts.cropSectionVarieties,
        ).map(([cropVarietyId, treeCount]) => ({ cropVarietyId, treeCount })),
      ),
    ];
  }, [
    activeRowIndexes,
    section,
    inRowPatternsMap,
    cropNameToId,
    cropVarietyIdToCropId,
  ]);

  const {
    rowLength,
    treeCount,
    inRowSpacings,
    rowCropIds,
    cropVarietyIds,
    patternIds,
  } = useMemo(
    () =>
      activeRowIndexes.reduce(
        (acc, rowIdx) => {
          const stats = section.rows.features[rowIdx].properties;
          const override = section.rowOverrides?.[rowIdx];

          const cropName =
            cropNameForRow(override, section) || UNKNOWN_CROP_NAME;

          // None rows should not be included in the row length
          if (cropName !== NONE) {
            acc.rowLength += stats.rowLength;
          }

          acc.treeCount += stats.treeCount;
          if (rowHasPattern(override, section)) {
            acc.patternIds.add(patternIdForRow(override, section));
            // Do not need to track crop varieties because variety selector will
            // not be shown if there is at least one pattern
          } else {
            const inRowSpacing = override?.inRowSpacing || section.inRowSpacing;
            acc.inRowSpacings.add(inRowSpacing);
            const cropId = getCropId({ name: cropName }, cropNameToId);
            acc.rowCropIds.add(cropId);
            const cropVarietyId = cropVarietyIdForRow(
              override,
              section,
              cropVarietyIdToCropId,
              cropId,
            );
            acc.cropVarietyIds.add(cropVarietyId);
          }

          return acc;
        },
        {
          rowLength: 0,
          treeCount: 0,
          inRowSpacings: new Set(),
          rowCropIds: new Set(),
          cropVarietyIds: new Set(),
          patternIds: new Set(),
        },
      ),
    [activeRowIndexes, section, cropNameToId, cropVarietyIdToCropId],
  );

  const allCrops = useMemo(() => {
    const allCropIds = new Set([...rowCropIds, ...Object.keys(cropCounts)]);
    const unsortedCrops = [...allCropIds].map((id) =>
      id === CROP_ID_FOR_NONE_CROP
        ? { id, name: NONE }
        : id === CROP_ID_FOR_UNASSIGNED_STEMS
        ? { id, name: UNKNOWN_CROP_NAME }
        : cropMap[id],
    );
    return sortBy(unsortedCrops, 'name');
  }, [cropMap, cropCounts, rowCropIds]);

  const hasCrops = rowCropIds.size > 0;
  const hasPatterns = patternIds.size > 0;

  // Show multiple crops selector if there are one or more patterns,
  // or more than one crop.
  const showMultipleCropsSelector = hasPatterns || rowCropIds.size > 1;
  // Show multiple patterns if there are crops or more than one pattern.
  // This only applies if there are any patterns present.
  const showMultiplePatterns = hasCrops || patternIds.size > 1;

  const hasMultipleInRowSpacings = inRowSpacings.size > 1;

  const onSelectPattern = (pattern) => {
    if (pattern.id === section.primaryInRowPattern?.id) {
      clearRowOverrides(section.id, activeRowIndexes);
    } else {
      captureEvent('pattern-added-to-row');
      setRowOverrides(section.id, activeRowIndexes, {
        inRowPatternId: pattern.id,
      });
    }
  };

  const isDesignTab = activeTab === rightMenuTabs.DESIGN;
  const isInspectTab = activeTab === rightMenuTabs.INSPECT;
  const showInRowGeometry = !hasPatterns && allCrops[0]?.name !== NONE;
  const showGeometrySection = showInRowGeometry && (readonly || isDesignTab);
  const showDesignData = !readonly && isDesignTab;
  const showInspectData = readonly || isInspectTab;

  const rowsTitle =
    activeRowIndexes.length === 1
      ? `Row ${activeRowIndexes[0] + 1}`
      : `${activeRowIndexes.length} Rows`;

  return (
    <>
      {!readonly && (
        <RightMenuTabs
          activeTab={activeTab}
          popOutEnabled
          setActiveTab={setActiveTab}
        />
      )}
      {!section.shown && <SectionHiddenWarning />}
      <PanelMainGroup>
        {(popOutTabOpen || showInspectData) && (
          <PopOutTabContainer title={`Inspect - ${rowsTitle}`}>
            <InspectTab
              activeRowIndexes={activeRowIndexes}
              allCrops={allCrops}
              colors={colors}
              cropCounts={cropCounts}
              cropVarietyCounts={cropVarietyCounts}
              readonly={readonly}
              rowLength={rowLength}
              treeCount={treeCount}
            />
          </PopOutTabContainer>
        )}
        {showGeometrySection && (
          <PanelSection>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4 className="m-0">Geometry</h4>
              {!readonly && (
                <PatternListDropdown onSelectPattern={onSelectPattern} />
              )}
            </div>
            {showInRowGeometry && (
              <LabeledInput
                label="in row"
                unitSpec={DISTANCE_UNITS}
                readonly={readonly}
                isValid={validators.isPositiveNumber}
                normalize={normalizers.asFloat}
                type="number"
                min={1}
                step="any"
                formatter={asRoundedDecimal}
                update={(value) => {
                  updateRowOverrides(section.id, activeRowIndexes, {
                    inRowSpacing: value,
                  });
                }}
                defaultValue={
                  hasMultipleInRowSpacings ? undefined : [...inRowSpacings][0]
                }
                placeholder={hasMultipleInRowSpacings ? MULTIPLE : undefined}
                icon={<InRowIcon />}
              />
            )}
          </PanelSection>
        )}
        {showDesignData && hasPatterns && (
          <PanelSection>
            <div className="d-flex justify-content-between align-items-center">
              <h4>Pattern</h4>
              <PatternListDropdown onSelectPattern={onSelectPattern} />
            </div>
            <LabeledData label="in row">
              <SelectedPatternDropdown
                patternName={
                  showMultiplePatterns
                    ? MULTIPLE
                    : inRowPatternsMap[[...patternIds][0]].name
                }
                patternId={
                  showMultiplePatterns ? undefined : [...patternIds][0]
                }
                onSelectPattern={onSelectPattern}
              />
            </LabeledData>
          </PanelSection>
        )}
        {showDesignData && (
          <CropDesignSection
            key={activeRowIndexes}
            activeRowIndexes={activeRowIndexes}
            colors={colors}
            crop={allCrops[0]}
            cropVarietyIds={cropVarietyIds}
            onSelectPattern={onSelectPattern}
            readonly={readonly}
            rowCropIds={rowCropIds}
            section={section}
            showPatternDropdown={!hasPatterns && !showGeometrySection}
            showMultipleCropsSelector={showMultipleCropsSelector}
          />
        )}
      </PanelMainGroup>
    </>
  );
};

export default RowMenu;
